import { BreakpointName, useBreakpoint } from "@hooks/useBreakpoint"
import { PeopleGridProvider } from "@hooks/usePeopleGrid"
import { cn } from "@utils/tw"
import { defineBlock, InnerBlocks, SingleContentBlock, useInnerBlocks } from "eddev/blocks"
import { useHydrating } from "eddev/routing"
import { AnimatePresence, motion, MotionConfig } from "motion/react"
import { Fragment, ReactNode, useState } from "react"

export const meta: BlockMeta = {
  title: "People Grid",
  category: "layouts",
  icon: "admin-users",
  description: "A grid of people with images, titles and bios.",
  tags: ["#subpage-layout"],
  flags: {
    fullWidth: true,
    marginRule: "long-card",
  },
}

const RESULTS_PER_ROW: { [K in BreakpointName]: number } = {
  BASE: 1,
  sm: 2,
  md: 3,
  lg: 3,
  xl: 3,
  xxl: 3,
}

export default defineBlock("content/people-grid", (props) => {
  const hydrating = useHydrating()
  const [expanderTarget, setExpanderTarget] = useState<HTMLDivElement | null>(null)

  let contents = null

  if (env.admin) {
    const [activeItem, setActiveItem] = useState<string>()
    return (
      <PeopleGridProvider
        value={{
          onSelect: (id) => {
            setActiveItem(id)
          },
          activeItem,
          renderTarget: expanderTarget,
        }}
      >
        <div>
          <div className="grid-auto">
            <div className="col-subpage-content-grid">
              <InnerBlocks
                allowedBlocks={["content/people-grid-item"]}
                defaultBlocks={[["content/people-grid-item", {}]]}
                appender={{ type: "simple" }}
                adminClassName={gridSize}
              />
            </div>
          </div>
          {env.admin && activeItem && (
            <div className="w-grid mx-auto my-6">
              <div ref={setExpanderTarget} />
            </div>
          )}
        </div>
      </PeopleGridProvider>
    )
  }

  if (!env.admin) {
    const innerBlocks = useInnerBlocks()
    const breakpoint = useBreakpoint()
    const perRow = RESULTS_PER_ROW[breakpoint]
    const [activeItem, setActiveItem] = useState<{ id: string; index: number }>()

    const rows = [] as ReactNode[]

    let previewAfterRow = Math.floor((activeItem?.index || 0) / perRow)

    if (hydrating) {
      rows.push(
        <div className={cn(gridSize, "gap-y-5 mb-5")}>
          {innerBlocks.map((block, i) => (
            <PeopleGridProvider
              key={i}
              value={{
                onSelect: (id) => {},
                activeItem: activeItem?.id,
                renderTarget: expanderTarget,
              }}
            >
              <SingleContentBlock block={block} />
            </PeopleGridProvider>
          ))}
        </div>,
      )
    } else {
      for (let i = 0; i <= Math.floor(innerBlocks.length / perRow); i += 1) {
        rows.push(
          <Fragment key={i}>
            <div className={cn(gridSize, "mb-5")}>
              {innerBlocks.slice(i * perRow, i * perRow + perRow).map((block, ii) => (
                <PeopleGridProvider
                  key={ii}
                  value={{
                    onSelect: (id) => {
                      if (id === activeItem?.id) {
                        setActiveItem(undefined)
                      } else {
                        setActiveItem({ id, index: i * perRow + ii })
                      }
                    },
                    activeItem: activeItem?.id,
                    renderTarget: expanderTarget,
                  }}
                >
                  <SingleContentBlock block={block} />
                </PeopleGridProvider>
              ))}
            </div>
          </Fragment>,
        )
      }
    }

    return (
      <div>
        {rows.map((row, i) => {
          return (
            <Fragment key={i}>
              <div className="col-subpage-content">{row}</div>
              {previewAfterRow === i && activeItem?.id && (
                <AnimatePresence key="preview">
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: "auto" }}
                    exit={{ height: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="w-grid mx-auto my-6">
                      <div ref={setExpanderTarget} />
                    </div>
                  </motion.div>
                </AnimatePresence>
              )}
            </Fragment>
          )
        })}
      </div>
    )
  }
})

const gridSize = cn("grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-grid-gutter")
